'use client'

import { CurrencyIcon } from '@repo/ui/CurrencyIcon'
import { EmailSingleIcon } from '@repo/ui/EmailSingleIcon'
import { FormulaIcon } from '@repo/ui/FormulaIcon'
import { NumbersIcon } from '@repo/ui/NumbersIcon'
import { TextEntry } from '@repo/ui/TextEntry'
import { TextIcon } from '@repo/ui/TextIcon'
import { UrlIcon } from '@repo/ui/UrlIcon'
import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

export const CustomDataFieldText = ({
  isInlineEditMode,
  field,
  value,
  onChange,
  validationErrors,
  fieldTypeIsString,
  fieldTypeIsNumber,
  size = 'small'
}) => {
  const { fieldType, scale, length, maxValue, minValue } = field || {}
  const [helperText, setHelperText] = useState(null)
  const [valueLength, setValueLength] = useState(String(value)?.length || 0)
  const [hasMaxValue] = useState(field?.maxValue || field?.maxValue === '0' || field?.maxValue === 0)
  const [hasMinValue] = useState(field?.minValue || field?.minValue === '0' || field?.minValue === 0)
  const inputRef = useRef(null)

  const getAdornment = () => {
    switch (fieldType) {
      case 'Integer':
      case 'Decimal':
        return <NumbersIcon sx={{ width: '18px', height: '18px' }} />
      case 'Money':
        return <CurrencyIcon sx={{ width: '14px', height: '14px' }} />
      case 'Email':
        return <EmailSingleIcon sx={{ width: '18px', height: '18px', opacity: 0.8 }} />
      case 'Text':
      case 'Multiline':
        return <TextIcon sx={{ width: '18px', height: '18px' }} />
      case 'Formula':
        return <FormulaIcon sx={{ width: '18px', height: '18px' }} />
      case 'URL':
        return <UrlIcon sx={{ width: '14px', height: '14px' }} />
      default:
        return null
    }
  }

  useEffect(() => {
    let helperTextArray = []

    if (fieldTypeIsNumber) {
      if (hasMinValue) helperTextArray.push(`Min value: ${minValue || 0}`)
      if (hasMaxValue) helperTextArray.push(`Max value: ${maxValue || 0}`)
      if (length) helperTextArray.push(`Max digits: ${length || 0}`)
    }

    if (fieldTypeIsString && length) {
      helperTextArray.push(`Characters: ${valueLength || 0} / ${length}`)
    }

    setHelperText(helperTextArray.join(', '))
  }, [fieldTypeIsNumber, fieldTypeIsString, length, minValue, maxValue, valueLength, hasMaxValue, hasMinValue])

  // On entering inline edit, the browser's autoFocus places the caret at position 0.
  // Move it to the end of the existing value instead (empty values naturally collapse to 0).
  useEffect(() => {
    if (isInlineEditMode && fieldTypeIsString && inputRef.current) {
      const caretPosition = inputRef.current.value?.length || 0
      inputRef.current.setSelectionRange(caretPosition, caretPosition)
    }
  }, [isInlineEditMode, fieldTypeIsString])

  const name = field?.fieldId || field?.customDataFieldId

  const getValueToDisplay = () => {
    if (fieldType === 'Formula') return 'Not editable'

    if (fieldTypeIsNumber && (value || value === 0 || value === '0')) return Number(value)

    return value
  }

  return (
    <TextEntry
      label=""
      value={getValueToDisplay()}
      onChange={onChange}
      isNumber={fieldType === 'Integer' || fieldType === 'Decimal' || fieldType === 'Money'}
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '4px',
        height: fieldType === 'Multiline' ? 'auto' : '40px',
        'input, textarea, .MuiInputBase-multiline, .MuiInputBase-root': { backgroundColor: 'white' },
        '&.MuiTextField-root': { backgroundColor: 'transparent !important' },
        '.MuiFormHelperText-root': {
          ml: 0,
          color: validationErrors ? 'error.dark' : 'text.primary',
          backgroundColor: 'transparent'
        },
        mb: helperText ? 4 : 0
      }}
      adornment={getAdornment()}
      size={size}
      autoFocus={isInlineEditMode}
      inputRef={fieldTypeIsString ? inputRef : undefined}
      name={name}
      errors={validationErrors ? { [name]: validationErrors } : null}
      showErrorInLabel={false}
      multiline={fieldType === 'Multiline'}
      rows={fieldType === 'Multiline' ? 6 : 1}
      readOnly={fieldType === 'Formula'}
      decimalPlaces={fieldType === 'Integer' ? 0 : scale}
      min={hasMinValue ? Number(minValue) : null}
      max={hasMaxValue ? Number(maxValue) : null}
      helperText={helperText}
      onKeyUp={(e) => setValueLength(e?.target?.value?.length || 0)}
    />
  )
}

CustomDataFieldText.propTypes = {
  isInlineEditMode: PropTypes.bool,
  field: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  validationErrors: PropTypes.array,
  fieldTypeIsString: PropTypes.bool,
  fieldTypeIsNumber: PropTypes.bool,
  fieldTypeIsAnId: PropTypes.bool,
  size: PropTypes.string
}
